import { EntryTypeEnum } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { normalizeType } from 'state/unified-menu/utils';

const isSectionType = (type?: string | null) =>
  normalizeType(type ?? undefined) === normalizeType(EntryTypeEnum.SECTION);

const isMenuType = (type?: string | null) =>
  normalizeType(type ?? undefined) === normalizeType(EntryTypeEnum.MENU);

/**
 * Finds the nearest SECTION parent of `entryId` by walking parent links.
 * Used when redirecting an unavailable PDP deeplink into its section list.
 */
export function findParentSectionId(
  entries: readonly ILocalizedMenuEntry[],
  entryId: string
): string | undefined {
  if (!entryId || !entries.length) {
    return undefined;
  }

  const byId = new Map(entries.map(entry => [entry.id, entry]));
  const parentByChildId = new Map<string, string>();
  for (const entry of entries) {
    for (const ref of entry.options?.entries ?? []) {
      if (!parentByChildId.has(ref.entryId)) {
        parentByChildId.set(ref.entryId, entry.id);
      }
    }
  }

  let currentId: string | undefined = entryId;
  const visited = new Set<string>();

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const parentId = parentByChildId.get(currentId);
    if (!parentId || parentId === currentId) {
      return undefined;
    }

    const parent = byId.get(parentId);
    if (!parent) {
      return undefined;
    }

    if (isSectionType(parent.type)) {
      return parent.id;
    }

    if (isMenuType(parent.type)) {
      return undefined;
    }

    currentId = parentId;
  }

  return undefined;
}

/**
 * Walks up to the top-level SECTION under the menu root (tab section).
 */
export function findTopLevelSectionId(
  entries: readonly ILocalizedMenuEntry[],
  entryId: string,
  rootMenuEntryId?: string | null
): string | undefined {
  if (!entryId || !entries.length) {
    return undefined;
  }

  const byId = new Map(entries.map(entry => [entry.id, entry]));
  const parentByChildId = new Map<string, string>();
  for (const entry of entries) {
    for (const ref of entry.options?.entries ?? []) {
      if (!parentByChildId.has(ref.entryId)) {
        parentByChildId.set(ref.entryId, entry.id);
      }
    }
  }

  let currentId: string | undefined = entryId;
  let lastSectionId: string | undefined;

  const visited = new Set<string>();
  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const entry = byId.get(currentId);
    if (!entry) {
      break;
    }

    if (isSectionType(entry.type)) {
      lastSectionId = entry.id;
    }

    if (rootMenuEntryId && currentId === rootMenuEntryId) {
      break;
    }

    const parentId = parentByChildId.get(currentId);
    if (!parentId || parentId === currentId) {
      break;
    }

    const parent = byId.get(parentId);
    if (isMenuType(parent?.type) || parentId === rootMenuEntryId) {
      break;
    }

    currentId = parentId;
  }

  return lastSectionId;
}
